import { forwardRef, type HTMLAttributes } from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { cn } from '@/utils/cn'

export interface ScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  orientation?: 'vertical' | 'horizontal' | 'both'
  dir: 'ltr' | 'rtl'
}

/**
 * Accessible scroll area with custom scrollbars.
 * Built on Radix UI ScrollArea for keyboard and assistive technology support.
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  function ScrollArea(
    { className, children, orientation = 'vertical', ...props },
    ref
  ) {
    return (
      <ScrollAreaPrimitive.Root
        className={cn('relative overflow-hidden', className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={ref}
          className="h-full w-full rounded-[inherit]"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>

        {(orientation === 'vertical' || orientation === 'both') && (
          <ScrollAreaPrimitive.Scrollbar
            orientation="vertical"
            className={cn(
              'flex touch-none select-none p-0.5 transition-colors',
              'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2.5',
              'hover:bg-gray-100'
            )}
          >
            <ScrollAreaPrimitive.Thumb
              className={cn(
                'relative flex-1 rounded-full bg-gray-300',
                'before:absolute before:left-1/2 before:top-1/2',
                'before:h-full before:min-h-[44px] before:w-full before:min-w-[44px]',
                'before:-translate-x-1/2 before:-translate-y-1/2'
              )}
            />
          </ScrollAreaPrimitive.Scrollbar>
        )}

        {(orientation === 'horizontal' || orientation === 'both') && (
          <ScrollAreaPrimitive.Scrollbar
            orientation="horizontal"
            className={cn(
              'flex touch-none select-none p-0.5 transition-colors',
              'data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:w-full',
              'data-[orientation=horizontal]:flex-col',
              'hover:bg-gray-100'
            )}
          >
            <ScrollAreaPrimitive.Thumb
              className={cn(
                'relative flex-1 rounded-full bg-gray-300',
                'before:absolute before:left-1/2 before:top-1/2',
                'before:h-full before:min-h-[44px] before:w-full before:min-w-[44px]',
                'before:-translate-x-1/2 before:-translate-y-1/2'
              )}
            />
          </ScrollAreaPrimitive.Scrollbar>
        )}

        <ScrollAreaPrimitive.Corner className="bg-gray-100" />
      </ScrollAreaPrimitive.Root>
    )
  }
)
